import type { PaginationProps } from 'antd';
import type { SelectProps } from 'antd';
import type { TablePaginationConfig } from 'antd/es/table';
import type { TFunction } from 'i18next';

export const EVIMO_TABLE_PAGE_SIZE_OPTIONS = ['10', '20', '50', '100'] as const;

export const EVIMO_TABLE_DEFAULT_PAGE_SIZE = 10;

/** Preset options only — no free-text page size (antd Pagination size changer). */
export const EVIMO_TABLE_PAGE_SIZE_CHANGER_SELECT_PROPS: SelectProps = {
  showSearch: false,
  popupMatchSelectWidth: false,
  getPopupContainer: () => document.body,
  popupClassName: 'evimo-table-page-size-dropdown',
};

export function mergeEvimoTableShowSizeChanger(
  showSizeChanger:
    | TablePaginationConfig['showSizeChanger']
    | PaginationProps['showSizeChanger'],
): TablePaginationConfig['showSizeChanger'] {
  if (showSizeChanger === false) {
    return false;
  }
  const userProps =
    typeof showSizeChanger === 'object' && showSizeChanger !== null
      ? showSizeChanger
      : {};
  return {
    ...EVIMO_TABLE_PAGE_SIZE_CHANGER_SELECT_PROPS,
    ...userProps,
    showSearch: userProps.showSearch ?? false,
  };
}

export type EvimoPaginationLiveState = {
  pageSize: number;
  current: number;
};

export function evimoTableTotalPages(total: number, pageSize: number): number {
  if (total <= 0) {
    return 1;
  }
  return Math.max(1, Math.ceil(total / pageSize));
}

export function evimoTablePageFromRange(
  rangeStart: number,
  pageSize: number,
  total: number,
): number {
  if (total <= 0 || rangeStart <= 0) {
    return 1;
  }
  return Math.floor((rangeStart - 1) / pageSize) + 1;
}

/** Prefer a full page's row count; fall back to configured page size on the last page. */
export function inferPaginationPageSize(
  range: [number, number],
  total: number,
  configuredPageSize: number,
): number {
  if (total <= 0) {
    return configuredPageSize;
  }
  const start = range[0];
  const end = range[1];
  if (start <= 0 || end < start) {
    return configuredPageSize;
  }
  const rowsOnPage = end - start + 1;
  if (end < total && rowsOnPage > 0) {
    return rowsOnPage;
  }
  return configuredPageSize;
}

export function formatEvimoTablePaginationSummary(
  t: TFunction,
  total: number,
  range: [number, number],
  getLiveState: () => EvimoPaginationLiveState,
): string {
  const live = getLiveState();
  const configuredPageSize = live.pageSize ?? EVIMO_TABLE_DEFAULT_PAGE_SIZE;
  const pageSize = inferPaginationPageSize(range, total, configuredPageSize);
  const start = total === 0 ? 0 : range[0];
  const end = total === 0 ? 0 : range[1];
  const page = live.current ?? evimoTablePageFromRange(start, pageSize, total);
  const totalPages = evimoTableTotalPages(total, pageSize);

  return t('tablePagination.summary', {
    defaultValue:
      '{{start}}–{{end}} of {{total}} · Page {{page}} of {{totalPages}}',
    start,
    end,
    total,
    page,
    totalPages,
  });
}

export function createEvimoTableShowTotal(
  t: TFunction,
  getLiveState: () => EvimoPaginationLiveState,
): NonNullable<TablePaginationConfig['showTotal']> {
  return (total, range) =>
    formatEvimoTablePaginationSummary(
      t,
      total,
      range as [number, number],
      getLiveState,
    );
}

export function mergeEvimoTablePagination(
  pagination: TablePaginationConfig | false | undefined,
  t: TFunction,
  liveState?: { current: EvimoPaginationLiveState },
): TablePaginationConfig | false {
  if (pagination === false) {
    return false;
  }

  const config: TablePaginationConfig = pagination ?? {};
  const { showSizeChanger: userShowSizeChanger, ...restConfig } = config;
  const pageSize = config.pageSize ?? EVIMO_TABLE_DEFAULT_PAGE_SIZE;
  const current = config.current ?? 1;

  if (liveState) {
    liveState.current = { pageSize, current };
  }

  const getLiveState = () => liveState?.current ?? { pageSize, current };

  const showSizeChanger =
    userShowSizeChanger === undefined
      ? mergeEvimoTableShowSizeChanger(true)
      : mergeEvimoTableShowSizeChanger(userShowSizeChanger);

  return {
    size: 'small',
    pageSizeOptions: [...EVIMO_TABLE_PAGE_SIZE_OPTIONS],
    ...restConfig,
    showTotal: config.showTotal ?? createEvimoTableShowTotal(t, getLiveState),
    showSizeChanger,
  };
}

export function mergeEvimoPaginationProps(
  props: PaginationProps,
  t: TFunction,
  liveState?: { current: EvimoPaginationLiveState },
): PaginationProps {
  const { showSizeChanger: userShowSizeChanger, ...restProps } = props;
  const pageSize = props.pageSize ?? EVIMO_TABLE_DEFAULT_PAGE_SIZE;
  const current = props.current ?? 1;

  if (liveState) {
    liveState.current = { pageSize, current };
  }

  const getLiveState = () => liveState?.current ?? { pageSize, current };

  const showSizeChanger =
    userShowSizeChanger === undefined
      ? mergeEvimoTableShowSizeChanger(true)
      : mergeEvimoTableShowSizeChanger(userShowSizeChanger);

  return {
    size: 'small',
    pageSizeOptions: [...EVIMO_TABLE_PAGE_SIZE_OPTIONS],
    ...restProps,
    showTotal:
      props.showTotal ??
      ((total, range) =>
        formatEvimoTablePaginationSummary(
          t,
          total,
          range as [number, number],
          getLiveState,
        )),
    showSizeChanger,
  };
}
